import { useMemo } from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import type { ScatterPlotCustomSvgLayerProps } from '@nivo/scatterplot';
import type { GeoBrowseVolcanoPoint, GeoBrowseThresholds } from '../../types';
import styles from '../../GeoBrowse.module.css';

/** Nature / EnhancedVolcano-style palette */
const COLORS = {
  ns: '#bdbdbd',
  up: '#e64b35',
  down: '#4dbbd5',
} as const;

const CHART_THEME = {
  axis: {
    ticks: { text: { fontSize: 11, fill: '#6b7280' } },
    legend: { text: { fontSize: 12, fill: '#374151', fontWeight: 500 } },
  },
  grid: { line: { stroke: '#f0f0f0', strokeWidth: 1 } },
};

const PRIORITY_GENES = new Set(['MMP3', 'MMP1', 'CHI3L1', 'IGHG4', 'IGHG3', 'LOC102724621']);

type VolcanoPlotProps = {
  points: GeoBrowseVolcanoPoint[];
  thresholds: GeoBrowseThresholds;
};

type VolcanoSeries = 'ns' | 'up' | 'down';

type VolcanoDatum = {
  x: number;
  y: number;
  id: string;
  series: VolcanoSeries;
  label?: string;
};

function toNegLog10Padj(padj: number): number {
  return -Math.log10(Math.max(padj, 1e-300));
}

function classifyPoint(p: GeoBrowseVolcanoPoint): VolcanoSeries {
  if (!p.significant) return 'ns';
  return p.log2FoldChange >= 0 ? 'up' : 'down';
}

function selectLabelGenes(points: GeoBrowseVolcanoPoint[], max = 10): Set<string> {
  const ranked = points
    .filter(p => p.significant)
    .map(p => ({
      symbol: p.gene_symbol,
      score: toNegLog10Padj(p.padj) * Math.abs(p.log2FoldChange),
      priority: PRIORITY_GENES.has(p.gene_symbol),
    }))
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority ? -1 : 1;
      return b.score - a.score;
    })
    .slice(0, max);

  return new Set(ranked.map(r => r.symbol));
}

function ThresholdLines({
  innerWidth,
  innerHeight,
  xScale,
  yScale,
  thresholds,
}: ScatterPlotCustomSvgLayerProps<VolcanoDatum> & { thresholds: GeoBrowseThresholds }) {
  const yThreshold = -Math.log10(thresholds.padj);
  return (
    <g>
      <line
        x1={xScale(-thresholds.fc)}
        x2={xScale(-thresholds.fc)}
        y1={0}
        y2={innerHeight}
        stroke="#9ca3af"
        strokeWidth={1}
        strokeDasharray="5 4"
      />
      <line
        x1={xScale(thresholds.fc)}
        x2={xScale(thresholds.fc)}
        y1={0}
        y2={innerHeight}
        stroke="#9ca3af"
        strokeWidth={1}
        strokeDasharray="5 4"
      />
      <line
        x1={0}
        x2={innerWidth}
        y1={yScale(yThreshold)}
        y2={yScale(yThreshold)}
        stroke="#9ca3af"
        strokeWidth={1}
        strokeDasharray="5 4"
      />
    </g>
  );
}

function VolcanoNodes({ nodes }: ScatterPlotCustomSvgLayerProps<VolcanoDatum>) {
  return (
    <g>
      {nodes.map(node => {
        const series = node.data.series;
        const radius = series === 'ns' ? 2.5 : 4;
        const opacity = series === 'ns' ? 0.5 : 0.9;
        return (
          <circle
            key={String(node.id)}
            cx={node.x}
            cy={node.y}
            r={radius}
            fill={COLORS[series]}
            fillOpacity={opacity}
          />
        );
      })}
    </g>
  );
}

function CountAnnotations({
  upCount,
  downCount,
  nsCount,
}: {
  upCount: number;
  downCount: number;
  nsCount: number;
}) {
  return (
    <g transform="translate(4, 14)">
      <text fontSize={11} fill="#374151" fontWeight={600}>
        <tspan fill={COLORS.up}>Up: {upCount}</tspan>
        <tspan fill="#9ca3af"> · </tspan>
        <tspan fill={COLORS.down}>Down: {downCount}</tspan>
        <tspan fill="#9ca3af"> · </tspan>
        <tspan fill="#6b7280">NS: {nsCount}</tspan>
      </text>
    </g>
  );
}

function GeneLabels({ nodes }: ScatterPlotCustomSvgLayerProps<VolcanoDatum>) {
  const labeled = nodes.filter(n => n.data.label);
  return (
    <g>
      {labeled.map((node, i) => {
        const isUp = node.data.series === 'up';
        const dx = isUp ? 7 : -7;
        const dy = i % 2 === 0 ? -5 : -13;
        return (
          <text
            key={String(node.id)}
            x={node.x + dx}
            y={node.y + dy}
            fontSize={10}
            fontWeight={500}
            fill={isUp ? COLORS.up : COLORS.down}
            textAnchor={isUp ? 'start' : 'end'}
          >
            {node.data.label}
          </text>
        );
      })}
    </g>
  );
}

export default function VolcanoPlot({ points, thresholds }: VolcanoPlotProps) {
  const labelGenes = useMemo(() => selectLabelGenes(points), [points]);

  const { chartData, upCount, downCount, nsCount, yMax } = useMemo(() => {
    const ns: VolcanoDatum[] = [];
    const up: VolcanoDatum[] = [];
    const down: VolcanoDatum[] = [];

    for (const p of points) {
      const series = classifyPoint(p);
      const datum: VolcanoDatum = {
        x: p.log2FoldChange,
        y: toNegLog10Padj(p.padj),
        id: p.gene_symbol,
        series,
        label: labelGenes.has(p.gene_symbol) ? p.gene_symbol : undefined,
      };
      if (series === 'up') up.push(datum);
      else if (series === 'down') down.push(datum);
      else ns.push(datum);
    }

    const maxY = Math.max(...points.map(p => toNegLog10Padj(p.padj)), 8);

    return {
      chartData: [
        { id: 'Not significant', data: ns },
        { id: 'Up-regulated', data: up },
        { id: 'Down-regulated', data: down },
      ],
      upCount: up.length,
      downCount: down.length,
      nsCount: ns.length,
      yMax: Math.ceil(maxY + 1),
    };
  }, [points, labelGenes]);

  return (
    <div className={styles.chartArea}>
      <ResponsiveScatterPlot
        data={chartData}
        margin={{ top: 28, right: 130, bottom: 52, left: 60 }}
        xScale={{ type: 'linear', min: -6, max: 6 }}
        yScale={{ type: 'linear', min: 0, max: yMax }}
        axisBottom={{
          legend: 'log₂ fold change',
          legendOffset: 40,
          legendPosition: 'middle',
          tickSize: 0,
          tickPadding: 8,
        }}
        axisLeft={{
          legend: '−log₁₀ adjusted p-value',
          legendOffset: -48,
          legendPosition: 'middle',
          tickSize: 0,
          tickPadding: 8,
        }}
        nodeSize={6}
        theme={CHART_THEME}
        useMesh
        enableGridX={false}
        colors={[COLORS.ns, COLORS.up, COLORS.down]}
        legends={[
          {
            anchor: 'top-right',
            direction: 'column',
            translateX: 120,
            itemWidth: 110,
            itemHeight: 18,
            symbolSize: 10,
            symbolShape: 'circle',
          },
        ]}
        layers={[
          'grid',
          'axes',
          props => <ThresholdLines {...props} thresholds={thresholds} />,
          VolcanoNodes,
          'mesh',
          GeneLabels,
          props => <CountAnnotations upCount={upCount} downCount={downCount} nsCount={nsCount} />,
          'legends',
        ]}
        tooltip={({ node }) => {
          const series = node.data.series as VolcanoSeries;
          const direction =
            series === 'up' ? 'Up-regulated' : series === 'down' ? 'Down-regulated' : 'Not significant';
          return (
            <div className={styles.volcanoTooltip}>
              <strong>{node.data.id}</strong>
              <div>{direction}</div>
              <div>log₂FC: {Number(node.data.x).toFixed(2)}</div>
              <div>−log₁₀ padj: {Number(node.data.y).toFixed(2)}</div>
            </div>
          );
        }}
      />
    </div>
  );
}
